"use client"

import { useQueryClient } from "@tanstack/react-query"
import { useQueryState, useQueryStates } from "nuqs"
import { Activity } from "react"
import {
  storeProductsQueryParsers,
  storeReviewsPageParser,
  storeTabParser,
} from "@/components/e-commerce/store/query-params/client"
import {
  buildStoreProductsQuerySeed,
  type StoreProductsPageLoader,
  type StoreProductsQuery,
} from "@/components/e-commerce/store/tabs/query"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { StoreProductsList } from "./products-list"
import { getStoreReviewsQueryOptions, StoreReviews } from "./reviews"

interface StoreTabsProps {
  onLoadMoreProducts: StoreProductsPageLoader
  storeId: number
  storeSlug: string
}

export const StoreTabs = ({
  onLoadMoreProducts,
  storeId,
  storeSlug,
}: StoreTabsProps): React.ReactNode => {
  const queryClient = useQueryClient()
  const [tab, setTab] = useQueryState("tab", storeTabParser)
  const [productsQuery] = useQueryStates(storeProductsQueryParsers, {
    history: "replace",
    shallow: true,
  })
  const [reviewsQuery] = useQueryStates(
    { reviews_page: storeReviewsPageParser },
    {
      history: "push",
      shallow: true,
    }
  )

  const normalizedQueryText = productsQuery.q.trim()
  const productsQueryState: StoreProductsQuery = {
    q: normalizedQueryText.length > 0 ? normalizedQueryText : undefined,
    orderby: productsQuery.orderby,
    order: productsQuery.order,
    per_page: productsQuery.per_page,
  }
  const productsQueryKeySeed = buildStoreProductsQuerySeed({
    storeId,
    storeSlug,
    query: productsQueryState,
  })

  const prefetchStoreReviews = (): void => {
    if (!(Number.isInteger(storeId) && storeId > 0)) {
      return
    }

    queryClient
      .prefetchQuery(
        getStoreReviewsQueryOptions({
          page: reviewsQuery.reviews_page,
          storeId,
        })
      )
      .catch(() => undefined)
  }

  return (
    <Tabs
      className='w-full'
      onValueChange={(value) => setTab(value as "products" | "reviews")}
      value={tab}
    >
      <TabsList>
        <TabsTrigger value='products'>Produtos</TabsTrigger>
        <TabsTrigger
          onClick={prefetchStoreReviews}
          onFocus={prefetchStoreReviews}
          onMouseEnter={prefetchStoreReviews}
          value='reviews'
        >
          Comentarios
        </TabsTrigger>
      </TabsList>

      <TabsContent forceMount value='products'>
        <Activity mode={tab === "products" ? "visible" : "hidden"}>
          <StoreProductsList
            onLoadMoreProducts={onLoadMoreProducts}
            query={productsQueryState}
            queryKeySeed={productsQueryKeySeed}
          />
        </Activity>
      </TabsContent>
      <TabsContent forceMount value='reviews'>
        <Activity mode={tab === "reviews" ? "visible" : "hidden"}>
          <StoreReviews isActive={tab === "reviews"} storeId={storeId} />
        </Activity>
      </TabsContent>
    </Tabs>
  )
}
